import BRANDS from "@elit-hair/brands";
import { getLocalStorage, saveLocalStorage } from "@elit-hair/elit-tools";
import { DEFAULT_COLOR_SCHEMES, THEMES } from "@elit-hair/elit-ui";
import { createContext, useContext, useEffect, useState } from "react";

import { DATE_FORMAT, TIME_FORMAT } from "@/constants/date-time";
import { useCurrentSettings, useUpdateCurrentConfig } from "@/queries/setting.queries";

export enum SETTINGS_KEYS {
    THEME = "THEME",
    COLOR_SCHEME = "COLOR_SCHEME",
    LANGUAGE = "LANGUAGE",
    DATE_FORMAT = "DATE_FORMAT",
    TIME_FORMAT = "TIME_FORMAT",
    DARWIN_AI_ENABLED = "DARWIN_AI_ENABLED",
    AI_ANALYSIS_ENABLED = "AI_ANALYSIS_ENABLED",
    DIALER_VOLUME = "DIALER_VOLUME",
    SELECTED_BRAND = "SELECTED_BRAND",
    SIDEBAR_COLLAPSED = "SIDEBAR_COLLAPSED",
    HAS_SEEN_TOUR = "HAS_SEEN_TOUR",
}

// If you add new settings, don't forget to add them in the DTO for the remote-config in backend
// services/call-svc/src/dtos/remote-config.dto.ts
export type SettingsDefinition = {
    THEME: THEMES;
    COLOR_SCHEME: string;
    LANGUAGE: string;
    DATE_FORMAT: DATE_FORMAT;
    TIME_FORMAT: TIME_FORMAT;
    DARWIN_AI_ENABLED: boolean;
    AI_ANALYSIS_ENABLED: boolean;
    DIALER_VOLUME: number;
    SELECTED_BRAND: keyof typeof BRANDS | null;
    HAS_SEEN_TOUR: boolean;
    SIDEBAR_COLLAPSED: boolean;
};

export const defaultSettings: SettingsDefinition = {
    THEME: THEMES.LIGHT,
    COLOR_SCHEME: DEFAULT_COLOR_SCHEMES[0].value,
    LANGUAGE: "en",
    DATE_FORMAT: DATE_FORMAT.DD_MM_YYYY,
    TIME_FORMAT: TIME_FORMAT.TWENTY_FOUR_HOURS,
    DARWIN_AI_ENABLED: false,
    AI_ANALYSIS_ENABLED: false,
    DIALER_VOLUME: 0,
    SELECTED_BRAND: "elithair",
    HAS_SEEN_TOUR: false,
    SIDEBAR_COLLAPSED: false,
};

export const SettingsContext = createContext<{
    currentSettings: SettingsDefinition;
    setValue: (key: SETTINGS_KEYS, value: SettingsDefinition[SETTINGS_KEYS]) => void;
}>({
    currentSettings: defaultSettings,
    setValue: () => {},
});

const loadLocalSettings = (): SettingsDefinition => {
    const settings = { ...defaultSettings };

    (Object.keys(defaultSettings) as Array<SETTINGS_KEYS>).forEach((key) => {
        const value = getLocalSetting(key);
        if (value !== null && value !== undefined) {
            (settings as Record<SETTINGS_KEYS, SettingsDefinition[SETTINGS_KEYS]>)[key] = value;
        }
    });

    return settings;
};

export const getLocalSetting = <T extends SettingsDefinition[SETTINGS_KEYS]>(key: SETTINGS_KEYS) => {
    return getLocalStorage<T>(key, defaultSettings[key] as T);
};

// Socket
export type SettingsSocketEvents = {
    "config:updated": {
        adminUserId: string;
        config: SettingsDefinition;
        targetUserId: string;
        timestamp: string;
    };
};

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
    const { data: currentSettingsResponse } = useCurrentSettings();
    const { mutateAsync: updateCurrentConfig } = useUpdateCurrentConfig();

    const [currentSettings, setCurrentSettings] = useState<SettingsDefinition>(loadLocalSettings());

    // Parse partial config from backend (Some keys are undefined)
    const parsePartialConfig = (config: SettingsDefinition) => {
        const newSettings: Record<SETTINGS_KEYS, SettingsDefinition[SETTINGS_KEYS]> = {
            ...currentSettings,
        };
        // Avoid assinging null from backend
        Object.keys(config).forEach((key) => {
            const settingKey = key as SETTINGS_KEYS;
            if (config[settingKey] !== null && config[settingKey] !== undefined) {
                newSettings[settingKey] = config[settingKey] as SettingsDefinition[SETTINGS_KEYS];

                // Save remote-config values in Local Storage
                saveLocalStorage(settingKey, newSettings[settingKey]);
            }
        });
        return newSettings as SettingsDefinition;
    };

    useEffect(() => {
        if (currentSettingsResponse) {
            const newConfig = parsePartialConfig(currentSettingsResponse);
            setCurrentSettings(newConfig);
        }
    }, [currentSettingsResponse]);

    const setValue = (key: SETTINGS_KEYS, value: SettingsDefinition[SETTINGS_KEYS]) => {
        saveLocalStorage(key, value);
        setCurrentSettings((prev) => ({ ...prev, [key]: value }));
        updateCurrentConfig({ [key]: value });
    };

    return <SettingsContext.Provider value={{ currentSettings, setValue }}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
    return useContext(SettingsContext);
};
